import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Star, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_META = {
  google: { label: 'Google', color: '#4285F4', emoji: '🔵', subtitle: 'Most visible — boosts our local search ranking' },
  facebook: { label: 'Facebook', color: '#1877F2', emoji: '📘', subtitle: 'Share with your network' },
  instagram: { label: 'Instagram', color: '#E1306C', emoji: '📸', subtitle: 'Tag us in a post or story' },
  trustpilot: { label: 'Trustpilot', color: '#00B67A', emoji: '✅', subtitle: 'Verified-buyer reviews' },
  custom: { label: 'Other', color: '#6B7280', emoji: '🔗', subtitle: 'Our preferred review page' },
};

/**
 * Public smart-link page — opens from the email/SMS sent by Konnect.
 * Lets the customer pick which platform to leave a review on.
 */
export default function ReviewSmartLinkPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clicking, setClicking] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/r/${token}/info`);
        setInfo(data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Link not found.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const pickPlatform = async (platform) => {
    setClicking(platform);
    try {
      const { data } = await axios.post(`${API}/api/r/${token}/click`, { platform });
      // Redirect after a small visual confirmation
      setTimeout(() => { window.location.href = data.redirect_url; }, 250);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not open the platform link.');
      setClicking('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl border border-rose-100 p-7 text-center" data-testid="smart-link-error">
          <AlertTriangle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900">Link unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">{error || 'This review link is invalid or expired.'}</p>
        </div>
      </div>
    );
  }

  const platforms = Object.entries(info.links || {}).filter(([_, url]) => !!url);
  const first = (info.customer_name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full" data-testid="smart-link-page">
        <div className="bg-white rounded-3xl shadow-xl border border-amber-100 p-7 text-center">
          <div className="text-5xl mb-3">⭐⭐⭐⭐⭐</div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {first ? `Hi ${first} — ` : ''}share your experience?
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Your feedback helps <strong>{info.business_name}</strong> serve you better. Pick where you'd like to review:
          </p>

          {platforms.length === 0 ? (
            <p className="mt-6 text-sm text-rose-500" data-testid="no-platforms">
              No review platforms have been configured yet. Please contact us directly.
            </p>
          ) : (
            <div className="mt-6 space-y-2.5">
              {platforms.map(([key]) => {
                const meta = PLATFORM_META[key] || PLATFORM_META.custom;
                const isClicking = clicking === key;
                return (
                  <button
                    key={key}
                    onClick={() => pickPlatform(key)}
                    disabled={!!clicking}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-amber-400 hover:shadow-md transition-all bg-white disabled:opacity-50 group"
                    data-testid={`smart-link-${key}`}
                  >
                    <div className="text-2xl shrink-0">{meta.emoji}</div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
                      <div className="text-[11px] text-slate-500">{meta.subtitle}</div>
                    </div>
                    {isClicking ? (
                      <Loader2 className="h-4 w-4 animate-spin text-amber-500 shrink-0" />
                    ) : (
                      <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-amber-500 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <p className="text-[10px] text-slate-400 mt-6 leading-relaxed">
            By choosing a platform, you'll be redirected away from this page. Powered by Konnect Reputation.
          </p>
        </div>
      </div>
    </div>
  );
}
